import React, { useState, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button, 
  Alert 
} from '@mui/material';
import { Save, Edit } from '@mui/icons-material';

// Register custom font sizes
const Size = Quill.import('attributors/style/size');
Size.whitelist = ['8px', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px', '64px'];
Quill.register(Size, true);

const MobileScreenTextEditor = ({ userId, locationId, buttonColor = '#000000' }) => {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (userId && locationId) {
      fetchMobileScreenSettings();
    }
  }, [userId, locationId]);

  const fetchMobileScreenSettings = async () => {
    setFetchLoading(true);
    try {
      // Fetch location-specific mobile screen text
      const locationResponse = await axios.get(`/api/wayfinder/locations/${locationId}`);
      
      if (locationResponse.data.success && locationResponse.data.data) {
        const location = locationResponse.data.data;
        
        // If mobileScreenText is empty, populate with textDirections as default
        if (!location.mobileScreenText && location.textDirections) {
          setValue(location.textDirections);
        } else {
          setValue(location.mobileScreenText || '');
        }
      }
    } catch (error) {
      console.error('Error fetching mobile screen settings:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to load text directions' 
      });
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Save location-specific mobile screen text
      const locationResponse = await axios.put(
        `/api/wayfinder/locations/${locationId}`,
        { mobileScreenText: value }
      );

      if (locationResponse.data.success) {
        setMessage({ 
          type: 'success', 
          text: 'Mobile screen text saved successfully!' 
        });
        
        // Clear message after 3 seconds
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      }
    } catch (error) {
      console.error('Error saving mobile screen settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to save text directions' 
      });
    } finally {
      setLoading(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['8px', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px', '64px'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet',
    'indent',
    'direction', 'align',
    'link', 'image', 'video'
  ];

  if (fetchLoading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography>Loading text editor...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{
              color: '#000000',
              borderBottom: `2px solid ${buttonColor}`,
              pb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Edit />
            QR Code Mobile Screen Text
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Edit the text that will appear on the mobile screen when users scan this location's QR code.
            This text provides guidance specific to this location. If empty, it will default to the location's text directions.
          </Typography>
        </Box>

        {message.text && (
          <Alert 
            severity={message.type} 
            sx={{ mb: 2 }}
            onClose={() => setMessage({ type: '', text: '' })}
          >
            {message.text}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <ReactQuill
            theme="snow"
            value={value}
            onChange={setValue}
            modules={modules}
            formats={formats}
            style={{
              minHeight: '400px',
              marginBottom: '60px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
            }}
            placeholder="Enter text for this location's mobile screen..."
          />
          
          {/* Custom CSS for font size dropdown */}
          <style>{`
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="8px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="8px"]::before {
              content: '8px';
              font-size: 8px !important;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before {
              content: '10px';
              font-size: 10px !important;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before {
              content: '12px';
              font-size: 12px !important;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before {
              content: '14px';
              font-size: 14px !important;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before {
              content: '16px';
              font-size: 16px !important;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before {
              content: '18px';
              font-size: 18px !important;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="20px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before {
              content: '20px';
              font-size: 20px !important;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before {
              content: '24px';
              font-size: 24px !important;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="28px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="28px"]::before {
              content: '28px';
              font-size: 28px !important;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before {
              content: '32px';
              font-size: 32px !important;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="36px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="36px"]::before {
              content: '36px';
              font-size: 36px !important;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="48px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="48px"]::before {
              content: '48px';
              font-size: 48px !important;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="64px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="64px"]::before {
              content: '64px';
              font-size: 64px !important;
            }
          `}</style>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={fetchMobileScreenSettings}
            disabled={loading}
            sx={{ 
              borderColor: buttonColor, 
              color: buttonColor,
              '&:hover': {
                borderColor: buttonColor,
                backgroundColor: `${buttonColor}10`,
              }
            }}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            startIcon={<Save />}
            sx={{
              backgroundColor: buttonColor,
              '&:hover': {
                backgroundColor: buttonColor,
                opacity: 0.9,
              },
            }}
          >
            {loading ? 'Saving...' : 'Save Mobile Screen Text'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MobileScreenTextEditor;
